import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingSpinner from '../Common/LoadingSpinner';
import { 
    faImage, 
    faVideo,
    faTrash,
    faPenToSquare,
    faTimes,
    faCheck,
    faPodcast,
    faFile,
    faMagnifyingGlass,
    faCloudArrowUp,
    faExclamationCircle,
    faExclamationTriangle,
    faSpinner,
    faPlay,
    faCopy,
    faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import './GalleryDetail.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { galleryService } from '../../services/galleryService';
import apiHelper from '../../utils/apiHelper';
import ModalSendToMediaCentre from '../../modals/ModalSendToMediaCentre';

const GalleryDetail = ({ gallery, onClose, onSave, galleryItems = [], setGalleryItems, refreshGalleryItems, refreshGalleries }) => {
    const formatIrishDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date)) return 'N/A';
            
            return date.toLocaleDateString('en-IE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    const [editMode, setEditMode] = useState(false);
    const [galleryData, setGalleryData] = useState(gallery);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [previewItem, setPreviewItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8; // Show 8 items (2 rows of 4)
    const [isLoadingItems, setIsLoadingItems] = useState(true);
    const [duplicateFiles, setDuplicateFiles] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showSendModal, setShowSendModal] = useState(false);
    const [selectedMediaItem, setSelectedMediaItem] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setGalleryData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Helper function to validate file types for galleries
    const validateFiles = (files) => {
        const validFiles = [];
        const invalidFiles = [];
        
        // Define allowed file types for each gallery type
        const allowedTypes = {
            image: {
                extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
                mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
            },
            video: {
                extensions: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'],
                mimeTypes: ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-ms-wmv', 'video/x-matroska', 'video/webm']
            },
            podcast: {
                extensions: ['mp3', 'wav', 'm4a', 'ogg', 'flac', 'aac'],
                mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/flac', 'audio/aac']
            }
        };
        
        const galleryType = gallery.GalleryType?.toLowerCase() || gallery.type?.toLowerCase();
        const allowed = allowedTypes[galleryType] || { extensions: [], mimeTypes: [] };

        files.forEach(file => {
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.split('.').pop();
            const fileMimeType = file.type.toLowerCase();
            
            // Check if file type is allowed for this gallery
            const isExtensionAllowed = allowed.extensions.includes(fileExtension);
            const isMimeTypeAllowed = allowed.mimeTypes.some(mime => fileMimeType.startsWith(mime.split('/')[0]));
            
            if (isExtensionAllowed || isMimeTypeAllowed) {
                validFiles.push(file);
            } else {
                invalidFiles.push({
                    name: file.name,
                    reason: `${fileExtension.toUpperCase()} files are not allowed in ${galleryType} galleries. Allowed types: ${allowed.extensions.map(ext => ext.toUpperCase()).join(', ')}`
                });
            }
        });
        
        // Show error messages for invalid files
        if (invalidFiles.length > 0) {
            const errorMessages = invalidFiles.map(f => `• ${f.name}: ${f.reason}`).join('\n');
            toast.error(`File type validation failed:\n\n${errorMessages}`, {
                autoClose: 8000,
                style: { 
                    whiteSpace: 'pre-line',
                    maxWidth: '500px'
                }
            });
        }
        
        return validFiles;
    };

    const handleFileSelect = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        
        // FIRST: Validate file types
        const validFiles = validateFiles(selectedFiles);
        if (validFiles.length === 0) return; // All files were invalid
        
        // SECOND: Check for duplicates among valid files
        const existingFileNames = galleryItems.map(item => item.name.toLowerCase());
        const duplicates = [];
        const newFiles = [];
        const galleryType = gallery.GalleryType?.toLowerCase() || gallery.type?.toLowerCase();

        for (const file of validFiles) {
                     
            const fileName = file.name.toLowerCase();
            
            if (existingFileNames.includes(fileName)) {
                duplicates.push(file);
            } else {
                newFiles.push({
                    id: Math.random().toString(36).substr(2, 9),
                    file,
                    name: file.name,
                    type: galleryType,
                    progress: 0,
                    status: 'pending',
                    // Only create blob URLs for images to avoid memory issues with videos
                    preview: galleryType === 'image' ? URL.createObjectURL(file) : null
                });
            }
        }

        if (duplicates.length > 0) {
            setDuplicateFiles(duplicates);
            // Show modal for duplicate files
            const duplicateNames = duplicates.map(f => f.name).join(', ');
            if (window.confirm(
                `The following files already exist in this gallery:\n${duplicateNames}\n\nWould you like to rename them?`
            )) {
                // Add files with modified names
                const renamedFiles = duplicates.map(file => {
                    const nameParts = file.name.split('.');
                    const ext = nameParts.pop();
                    const baseName = nameParts.join('.');
                    const newName = `${baseName}_${Date.now()}.${ext}`;
                    
                    // Create new File object with modified name
                    const renamedFile = new File([file], newName, { type: file.type });
                    
                    const galleryType = gallery.GalleryType?.toLowerCase() || gallery.type?.toLowerCase();
                    
                    return {
                        id: Math.random().toString(36).substr(2, 9),
                        file: renamedFile,
                        name: newName,
                        type: galleryType, // Use the gallery's type instead of guessing
                        progress: 0,
                        status: 'pending',
                        // Only create blob URLs for images to avoid memory issues with videos
                        preview: galleryType === 'image' ? URL.createObjectURL(file) : null
                    };
                });
                
                setFiles(prev => [...prev, ...newFiles, ...renamedFiles]);
            } else {
                // Only add non-duplicate files
                setFiles(prev => [...prev, ...newFiles]);
            }
        } else {
            // No duplicates, add all files
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleUpload = async () => {
        if (!files.length) return;
        setUploading(true);

        try {
            for (const file of files) {
                
                const formData = new FormData();
                formData.append('file', file.file);
                formData.append('galleryId', gallery.id);

                const response = await galleryService.uploadMedia(formData);

                setFiles(prev => prev.filter(f => f.id !== file.id));
                
                // Show storage source in success message for debugging
                const storageInfo = response.storageSource ? ` (${response.storageSource})` : '';
                toast.success(`${file.name} uploaded successfully${storageInfo}`);
            }

            // After successful upload, refresh the gallery items
           
            
            // Clear the gallery service cache to ensure fresh data
            galleryService.clearCache();
            
            // Fetch fresh gallery items
            const freshItems = await galleryService.getGalleryMedia(gallery.id);
           
            
            setGalleryItems(freshItems || []);
            
            // Update gallery stats
            const updatedStats = await galleryService.getGalleryStats(gallery.id);

            
           
            if (onSave) {
                const updatedGallery = {
                    ...gallery,
                    itemCount: updatedStats.itemCount,
                    keepOpen: true
                };
               
                onSave(updatedGallery);
            }

            // Also call the refresh function if provided
            if (refreshGalleryItems) {
               
                await refreshGalleryItems(gallery.id);
            }

        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload files: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const removeFile = (fileId) => {
        const file = files.find(f => f.id === fileId);
        if (file && file.preview) {
            URL.revokeObjectURL(file.preview);
        }
        setFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleSave = async (isUpload = false) => {
        try {
            const updatedGallery = await galleryService.updateGallery(gallery.id, galleryData);
            toast.success('Gallery updated successfully');
            
            // If this save is from an upload, keep the modal open
            if (isUpload) {
                onSave({
                    ...updatedGallery,
                    keepOpen: true
                });
            } else {
                // Normal save should close the modal
                onSave(updatedGallery);
            }
            setEditMode(false);
        } catch (error) {
            console.error('Error updating gallery:', error);
            toast.error('Failed to update gallery');
        }
    };

    // Add a helper function to get the correct upload hint
    const getUploadHint = (galleryType) => {
        switch(galleryType?.toLowerCase()) {
            case 'image':
                return 'Accepts images (JPG, PNG, GIF)';
            case 'video':
                return 'Accepts videos (MP4, WebM)';
            case 'podcast':
                return 'Accepts audio (MP3, WAV)';
            default:
                return 'Select files to upload';
        }
    };

    // Add a helper function to get the correct accept attribute
    const getAcceptTypes = (galleryType) => {
        switch(galleryType?.toLowerCase()) {
            case 'image':
                return "image/*";
            case 'video':
                return "video/*";
            case 'podcast':
                return "audio/*";
            default:
                return "";
        }
    };

    // Add drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault(); // Prevent browser from opening the file
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const droppedFiles = Array.from(e.dataTransfer.files);
        
        // Validate file types
        const validFiles = validateFiles(droppedFiles);
        if (validFiles.length === 0) return; // All files were invalid
        
        const galleryType = gallery.GalleryType?.toLowerCase() || gallery.type?.toLowerCase();
        
        // Convert valid files to file objects for the UI
        const fileObjects = validFiles.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file,
            name: file.name,
            type: galleryType,
            progress: 0,
            status: 'pending',
            // Only create blob URLs for images to avoid memory issues with videos
            preview: galleryType === 'image' ? URL.createObjectURL(file) : null
        }));
        
        setFiles(prev => [...prev, ...fileObjects]);
    };

   // Clean up object URLs when component unmounts
    useEffect(() => {
        return () => {
            files.forEach(file => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, []);

    const getAllItems = () => {
       
        
        // Create a Map to store unique items by ID
        const uniqueItems = new Map();

        // Process existing items
        galleryItems.forEach(item => {
            if (!uniqueItems.has(item.id)) {  // Only add if not already present
                uniqueItems.set(item.id, {
                    ...item,
                    type: item.mimeType?.startsWith('image/') ? 'image' : 
                          item.mimeType?.startsWith('video/') ? 'video' : 
                          item.mimeType?.startsWith('audio/') ? 'podcast' : 'unknown',
                    preview: item.mediaUrl, // Use the mediaUrl from API (now includes streaming URLs)
                    uniqueKey: `existing-${item.id}`
                });
            }
        });

        // Process uploaded files
        uploadedFiles.forEach((file, index) => {
            const key = `new-${file.id}-${index}`;
            uniqueItems.set(key, {
                ...file,
                isNew: true,
                tempId: key,
                uniqueKey: key
            });
        });

        // Convert Map values back to array and sort by createdAt
        const allItems = Array.from(uniqueItems.values());
        const sortedItems = allItems.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB - dateA; // Newest first
        });
        
       
        return sortedItems;
    };

    const handleItemClick = (item) => {
        setPreviewItem(item);
    };

    const handleClosePreview = () => {
        setPreviewItem(null);
    };

    const getFilteredItems = () => {
        const allItems = getAllItems();
       
        
        const filteredItems = searchTerm 
            ? allItems.filter(item => 
                item.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : allItems;

       

        // Calculate pagination
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
        
        const result = {
            items: currentItems,
            totalItems: filteredItems.length,
            totalPages: Math.ceil(filteredItems.length / itemsPerPage)
        };
        
       
        return result;
    };

    // Update the handleDeleteItem function
    const handleDeleteItem = async (mediaId, e) => {
        e.stopPropagation(); // Prevent preview opening
        
        if (!window.confirm('Are you sure you want to delete this item? This only removes the item from this gallery, it does not delete the file from the storage.')) {
            return;
        }

        try {
            setIsLoadingItems(true);
            
            await galleryService.deleteGalleryItem(gallery.id, mediaId);
            
            // Show success notification
            toast.success('Item deleted successfully');

            // Force refresh of gallery items
            if (refreshGalleryItems) {
                await refreshGalleryItems(gallery.id);
            }

            // Update gallery stats and parent component
            const updatedStats = await galleryService.getGalleryStats(gallery.id);
            if (onSave) {
                onSave({
                    ...gallery,
                    itemCount: updatedStats.itemCount,
                    keepOpen: true
                });
            }

        } catch (error) {
            console.error('Error deleting item:', error);
            toast.error('Failed to delete item: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoadingItems(false);
        }
    };

    // Update the useEffect that handles loading gallery items
    useEffect(() => {
        const fetchGalleryItems = async () => {
            if (!gallery) {
                setIsLoadingItems(false);
                return;
            }
            
            setIsLoadingItems(true);
            try {
               
                const items = await galleryService.getGalleryMedia(gallery.id);
              
                setGalleryItems(items || []);
            } catch (error) {
                console.error('Error fetching gallery items:', error);
                toast.error('Failed to load gallery items');
                setGalleryItems([]);
            } finally {
                setIsLoadingItems(false);
            }
        };

        fetchGalleryItems();
    }, [gallery]); // Only depend on gallery

    // Add this function near your other handlers
    const handleDeleteGallery = async () => {
        
        if (window.confirm('Are you sure you want to delete this gallery and all its items?')) {
            try {
               
                await galleryService.deleteGallery(gallery.id);
               
                
                // Clear the gallery service cache
                galleryService.clearCache();
                
                // Show success message
                toast.success('Gallery deleted successfully');
                
                // Refresh galleries
                if (refreshGalleries) {
                   
                    await refreshGalleries();
                }   
                
                // Close the modal
                onClose();
            } catch (error) {
                console.error('Error deleting gallery:', error);
                toast.error('Failed to delete gallery');
            }
        }
    };

    // Add this function to handle item selection
    const handleItemSelect = (item) => {
        setSelectedItems(prev => {
            if (prev.includes(item.id)) {
                return prev.filter(id => id !== item.id);
            } else {
                return [...prev, item.id];
            }
        });
    };

    return (
        <div className="gallery-detail-container">
            <ToastContainer />
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div className="gallery-header">
                        <h3>{gallery.title}</h3>
                    </div>
                    <div className="header-actions">
                        {!editMode && (
                            <>
                                <Button 
                                    variant="danger" 
                                    onClick={handleDeleteGallery}
                                    className="me-2"
                                >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                </Button>
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => setEditMode(true)}
                                    className="edit-btn"
                                >
                                    <FontAwesomeIcon icon={faPenToSquare} /> Edit
                                </Button>
                            </>
                        )}
                        <Button 
                            variant="link" 
                            onClick={onClose} 
                            className="close-btn"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    </div>
                </Card.Header>

                <Card.Body>
                    {editMode ? (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Gallery Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    value={galleryData.title}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={galleryData.description}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={galleryData.status}
                                    onChange={handleInputChange}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                </Form.Select>
                            </Form.Group>

                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={() => setEditMode(false)}>
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={() => handleSave(false)}>
                                    Save Changes
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        <>
                            <div className="gallery-info-display">
                                <p>{gallery.description}</p>
                            </div>

                            {files.length > 0 && (
                                    <div className="upload-list mt-3">
                                        <div className="upload-status-banner">
                                            <div className="upload-status-content">
                                                <FontAwesomeIcon 
                                                    icon={faExclamationTriangle} 
                                                    className="upload-warning-icon"
                                                />
                                                <div className="upload-status-text">
                                                    <strong>{files.length} file{files.length > 1 ? 's' : ''} waiting to be uploaded</strong>
                                                    <span>Click "Upload Files" to add them to your gallery</span>
                                                </div>
                                                <Button 
                                                    variant="success" 
                                                    size="lg"
                                                    onClick={handleUpload}
                                                    disabled={uploading || files.every(f => f.status === 'completed')}
                                                    className="upload-prominent-btn"
                                                >
                                                    <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                                                    {uploading ? 'Uploading...' : 'Upload Files'}
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="upload-items-grid">
                                            {files.map(file => (
                                                <div key={file.id} className="upload-item">
                                                    <div className="file-preview">
                                                        {file.type === 'image' ? (
                                                            <img 
                                                                src={file.preview} 
                                                                alt={file.name}
                                                                className="preview-thumbnail"
                                                            />
                                                        ) : file.type === 'video' ? (
                                                            <div className="video-preview-placeholder">
                                                                <FontAwesomeIcon 
                                                                    icon={faVideo}
                                                                    className="video-preview-icon"
                                                                />
                                                                <span className="video-preview-text">Video</span>
                                                                <span className="video-filename">{file.name}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="file-preview-placeholder">
                                                                <FontAwesomeIcon 
                                                                    icon={file.type === 'podcast' ? faPodcast : faFile}
                                                                    className="preview-icon"
                                                                />
                                                                <span className="file-type-text">
                                                                    {file.type === 'podcast' ? 'Audio' : 'File'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="file-info">
                                                        <div className="file-details">
                                                            <Button 
                                                                variant="link" 
                                                                className="text-danger" 
                                                                onClick={() => removeFile(file.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </Button>
                                                            <span>{file.name}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            <div className="upload-section">
                                <div 
                                    className="upload-zone"
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                     <FontAwesomeIcon 
                                            icon={faCloudArrowUp} 
                                            size="2x" 
                                     
                                            style={{ marginBottom: '10px' }}
                                    />
                                    <input
                                        type="file"
                                        id="file-upload"
                                        multiple
                                        accept={getAcceptTypes(gallery.GalleryType)}
                                        onChange={handleFileSelect}
                                        className="d-none"
                                    />
                                    <label 
                                        htmlFor="file-upload" 
                                        className="upload-label"
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                    >
                                        
                                        <span>Drag & drop files or click to upload</span>
                                        <span className="upload-hint">
                                            {getUploadHint(gallery.GalleryType)}
                                        </span>
                                       
                                    </label>
                                </div>

                                

                                <div className="gallery-content mt-4">
                                    {/* Only show search when there are items */}
                                    {getFilteredItems().totalItems > 0 && (
                                        <div className="search-box-holder">
                                            <div className="search-box">
                                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {isLoadingItems ? (
                                        <div className="loading-container">
                                            <LoadingSpinner />
                                        </div>
                                    ) : getFilteredItems().totalItems === 0 ? (
                                        <div className="no-items-message text-center text-muted mt-4">
                                            <p>No items in this gallery yet.</p>
                                            <p>Upload some files to get started!</p>
                                        </div>
                                    ) : (
                                        <div className="gallery-items-grid">
                                            {getFilteredItems().items.map((item, index) => (
                                                <div 
                                                    key={`${item.id}-${index}`}
                                                    className={`gallery-detail-item ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                                                    onClick={() => !item.mimeType?.toLowerCase().startsWith('video/') && handleItemClick(item)}
                                                >
                                                    <MediaPreview 
                                                        item={item} 
                                                        onSendToMediaCentre={(mediaItem) => {
                                                                setSelectedMediaItem(mediaItem);
                                                            setShowSendModal(true);
                                                        }}
                                                    />
                                                    <div className="item-info">
                                                        <span className="item-name">{item.name || item.title}</span>
                                                        {item.description && (
                                                            <span className="item-description">{item.description}</span>
                                                        )}
                                                    </div>
                                                    <div className="gallery-detail-delete-actions">
                                                        <button 
                                                            className="gallery-detail-btn-icon" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteItem(item.id, e);
                                                            }}
                                                            title="Delete item"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add pagination controls */}
                                    {getFilteredItems().totalPages > 1 && (
                                        <div className="pagination-controls">
                                            <Button 
                                                variant="outline-secondary"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </Button>
                                            <span className="pagination-info">
                                                Page {currentPage} of {getFilteredItems().totalPages}
                                                {' '}({getFilteredItems().totalItems} items)
                                            </span>
                                            <Button 
                                                variant="outline-secondary"
                                                onClick={() => setCurrentPage(prev => 
                                                    Math.min(prev + 1, getFilteredItems().totalPages)
                                                )}
                                                disabled={currentPage === getFilteredItems().totalPages}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {previewItem && (
                                <div className="media-preview-overlay" onClick={handleClosePreview}>
                                    <div className="media-preview-content" onClick={e => e.stopPropagation()}>
                                        <button className="close-preview" onClick={handleClosePreview}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                        <MediaPreview 
                            item={previewItem} 
                            onSendToMediaCentre={(mediaItem) => {
                                setSelectedMediaItem(mediaItem);
                                setShowSendModal(true);
                            }}
                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
            
            {/* Send to Media Centre Modal */}
            <ModalSendToMediaCentre
                show={showSendModal}
                onHide={() => setShowSendModal(false)}
                mediaItem={selectedMediaItem}
            />
        </div>
    );
};

const MediaPreview = ({ item, onSendToMediaCentre }) => {
    const [mediaUrl, setMediaUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const isMounted = useRef(true);
    const currentLoadRef = useRef(null);

    const isVideo = item?.mimeType?.toLowerCase().startsWith('video/');
    const isAudio = item?.mimeType?.toLowerCase().startsWith('audio/') || 
                   (item?.name && /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(item.name));

    


    const loadMedia = async () => {
        if (!item?.mediaUrl) {
            setError('No media URL provided');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // For videos and audio, always use direct streaming URLs (no blob download)
            if (isVideo || isAudio) {
                
                const streamingUrl = item.mediaUrl?.startsWith('/media/') 
                    ? `${process.env.REACT_APP_API_URL}${item.mediaUrl}` 
                    : item.mediaUrl;
                setMediaUrl(streamingUrl);
                setError(null);
                setIsLoading(false);
                return;
            }

            // For images or API proxy URLs, fetch as blob
            const headers = await apiHelper.getHeaders('getMediaContent');
            const controller = new AbortController();
            currentLoadRef.current = controller;

            const response = await fetch(item.mediaUrl, { 
                headers,
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error(`Failed to load media: ${response.status}`);
            }

            if (!isMounted.current) return;

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            if (!isMounted.current) {
                URL.revokeObjectURL(url);
                return;
            }

            setMediaUrl(url);
            setError(null);
            setIsLoading(false);
        } catch (error) {
            if (!isMounted.current) return;
            if (error.name === 'AbortError') {
                return;
            }
            console.error('Error loading media:', error);
            setError(error.message);
            setIsLoading(false);
        }
    };

    // Load media on mount - for videos/audio, use direct URLs; for images, load as blob
    useEffect(() => {
        isMounted.current = true;

        // For videos and audio, use the streaming URL directly (supports range requests)
        if (isVideo || isAudio) {
           
            
            // For videos, use the full URL (unprotected streaming endpoint)
            // Always use direct streaming for videos - no blob download
            const streamingUrl = item.mediaUrl?.startsWith('/media/') 
                ? `${process.env.REACT_APP_API_URL}${item.mediaUrl}` 
                : item.mediaUrl;
           
            setMediaUrl(streamingUrl);
            setIsLoading(false);
            setError(null);
        }
        // For images, load as blob for better caching
        else {
            loadMedia();
        }

        return () => {
            isMounted.current = false;
            if (currentLoadRef.current) {
                currentLoadRef.current.abort();
                currentLoadRef.current = null;
            }
            // Only revoke blob URLs, not direct API URLs
            if (mediaUrl && mediaUrl.startsWith('blob:')) {
                URL.revokeObjectURL(mediaUrl);
            }
        };
    }, [item?.mediaUrl]);

    const handlePlayClick = async (e) => {
        e.stopPropagation();
        
        // If we don't have a mediaUrl yet, load it
        if (!mediaUrl) {
            await loadMedia();
        }
        
        setIsPlaying(true);
    };

    const handleCopyUrl = async (e) => {
        e.stopPropagation();
        
        try {
            const urlToCopy = mediaUrl || (item.mediaUrl?.startsWith('/media/') 
                ? `${process.env.REACT_APP_API_URL}${item.mediaUrl}` 
                : item.mediaUrl);
            
            if (urlToCopy) {
                await navigator.clipboard.writeText(urlToCopy);
                toast.success('Video URL copied to clipboard!');
            } else {
                toast.error('No URL available to copy');
            }
        } catch (error) {
            console.error('Failed to copy URL:', error);
            toast.error('Failed to copy URL to clipboard');
        }
    };

    const handleSendToMediaCentre = (e, mediaItem) => {
        e.stopPropagation();
       
        
        // Enhance the media item with type information from mime type and filename
        const isVideo = mediaItem.mimeType?.startsWith('video/');
        const isAudio = mediaItem.mimeType?.startsWith('audio/') || 
                       (mediaItem.name && /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(mediaItem.name));
        const isImage = mediaItem.mimeType?.startsWith('image/');
        
        const enhancedMediaItem = {
            ...mediaItem,
            type: isVideo ? 'video' : 
                  isAudio ? 'Audio' : 
                  isImage ? 'image' : 'unknown',
            mediaUrl: mediaItem.mediaUrl || mediaItem.preview
        };
        
        
        if (onSendToMediaCentre) {
           
            onSendToMediaCentre(enhancedMediaItem);
        } 
    };

    if (error) {
        return (
            <div className="media-preview error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>Error: {error}</span>
            </div>
        );
    }

    return (
        <div className="media-preview">
            {isLoading ? (
                <LoadingSpinner />
            ) : isVideo ? (
                <div className="video-preview">
                    {isPlaying && mediaUrl ? (
                        <div className="video-player-container">
                            <div className="video-player-header">
                                <button 
                                    className="stop-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsPlaying(false);
                                    }}
                                    title="Stop Video"
                                >
                                    <FontAwesomeIcon icon={faTimes} /> Stop
                                </button>
                            </div>
                            <video 
                                src={mediaUrl}
                                controls
                                autoPlay
                                className="video-preview-player"
                                onEnded={() => setIsPlaying(false)}
                                onError={(e) => console.error('Video error:', e)}
                            />
                        </div>
                    ) : (
                        <div className="video-placeholder">
                            <FontAwesomeIcon icon={faVideo} className="video-icon" />
                            <div className="video-controls">
                                <button 
                                    className="play-button compact"
                                    onClick={handlePlayClick}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faPlay} /> Play
                                        </>
                                    )}
                                </button>
                                <button 
                                    className="send-to-media-centre-button compact"
                                    onClick={(e) => handleSendToMediaCentre(e, item)}
                                    title="Send to Media Centre"
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} /> Media Centre
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : isAudio ? (
                <div className="audio-preview">
                    {isPlaying && mediaUrl ? (
                        <div className="audio-player">
                            <div className="audio-player-header">
                                <FontAwesomeIcon icon={faPodcast} className="icon" />
                                <span className="title">{item.name}</span>
                                <button 
                                    className="stop-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsPlaying(false);
                                    }}
                                    title="Stop Audio"
                                >
                                    <FontAwesomeIcon icon={faTimes} /> Stop
                                </button>
                            </div>
                            <audio 
                                src={mediaUrl}
                                controls
                                autoPlay
                                className="audio-preview-player"
                                onEnded={() => setIsPlaying(false)}
                            />
                        </div>
                    ) : (
                        <div className="audio-placeholder">
                            <FontAwesomeIcon icon={faPodcast} className="audio-icon" />
                           
                            <div className="audio-controls">
                                <button 
                                    className="play-button compact"
                                    onClick={handlePlayClick}
                                >
                                    <FontAwesomeIcon icon={faPlay} /> Play
                                </button>
                                <button 
                                    className="send-to-media-centre-button compact"
                                    onClick={(e) => handleSendToMediaCentre(e, item)}
                                    title="Send to Media Centre"
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} /> Media Centre
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="image-preview">
                    <img 
                        src={mediaUrl} 
                        alt={item.title || item.name || 'Gallery item'} 
                        onError={(e) => {
                            console.error('Image load error:', e);
                            setError('Failed to load image');
                        }}
                    />
                    <div className="image-overlay">
                        <button 
                            className="send-to-media-centre-button compact"
                            onClick={(e) => handleSendToMediaCentre(e, item)}
                            title="Send to Media Centre"
                        >
                            <FontAwesomeIcon icon={faPaperPlane} /> Media Centre
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryDetail; 